/**
 * Plot Matrix (JSON) dev-tool — "Valuation Stage View" collapse.
 *
 * Mirrors `PlotMatrix.tsx`'s own stage-view `useMemo` (collapse every
 * sub-column into one column per Valuation Stage, 2dp-rounded per cell so
 * the stage total, column total and row total all agree) — reuses the same
 * `collapsePlotMatrixStageName` rule already shared with the Dynamic Bill
 * Summary → Plot Matrix mapping feature, rather than re-deriving it.
 */
import { collapsePlotMatrixStageName } from './dynamicBillSummaryPlotMatrix';
import type { PlotMatrixCategoryColumn, PlotMatrixRow } from './plotMatrixCategoryGenerator';

export interface PlotMatrixStageViewRow {
  key: string;
  plotNo: string;
  housetype: string;
  rowType: 'plot' | 'garage';
  stageTotals: Record<string, number>;
  rowTotal: number;
}

export interface PlotMatrixStageViewResult {
  stageNames: string[];
  rows: PlotMatrixStageViewRow[];
  stageTotals: Record<string, number>;
  grandTotal: number;
}

export function buildPlotMatrixStageView(
  columns: PlotMatrixCategoryColumn[],
  rows: PlotMatrixRow[],
): PlotMatrixStageViewResult {
  const stageNames: string[] = [];
  const columnsByStage = new Map<string, PlotMatrixCategoryColumn[]>();

  columns.forEach((column) => {
    const stage = collapsePlotMatrixStageName(column.categoryName);
    if (!columnsByStage.has(stage)) {
      columnsByStage.set(stage, []);
      stageNames.push(stage);
    }
    columnsByStage.get(stage)!.push(column);
  });

  const stageRows: PlotMatrixStageViewRow[] = rows.map((row) => {
    const stageTotals: Record<string, number> = {};
    let rowTotal = 0;
    stageNames.forEach((stage) => {
      const sum = (columnsByStage.get(stage) ?? []).reduce((acc, col) => {
        const cell = (row as unknown as Record<string, { total?: number } | undefined>)[col.dataIndex];
        return acc + (cell?.total ?? 0);
      }, 0);
      const rounded = parseFloat(sum.toFixed(2));
      stageTotals[stage] = rounded;
      rowTotal += rounded;
    });
    return {
      key: row.key,
      plotNo: row.plotNo,
      housetype: row.housetype,
      rowType: row.rowType,
      stageTotals,
      rowTotal: parseFloat(rowTotal.toFixed(2)),
    };
  });

  const stageTotals: Record<string, number> = {};
  stageNames.forEach((stage) => {
    stageTotals[stage] = stageRows.reduce((sum, row) => sum + (row.stageTotals[stage] || 0), 0);
  });
  const grandTotal = Object.values(stageTotals).reduce((sum, value) => sum + value, 0);

  return { stageNames, rows: stageRows, stageTotals, grandTotal };
}
